import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import type { FC, ReactNode } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import useWhiteLabelConfig from "src/contexts/config-context";
import useUserDataProvider from "src/contexts/userData-context";
import type { NavColor } from "src/types/settings";
import Typography from "@mui/material/Typography";
import { useParams } from "react-router-dom";
import useHousehold from "src/contexts/household-context";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { RouterLink } from "src/components/router-link";
import { paths } from "src/paths";
import { useNavigate } from "react-router-dom";
import { usePathname } from "src/hooks/use-pathname";
import Confetti from "react-confetti";
import toast from "react-hot-toast";
import { AccountButton } from "../account-button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { PlatformTrialDialog } from "src/sections/dashboard/platform-trial-dialog";

// const TOP_NAV_HEIGHT: number = 64;

// Create context for express layout features
interface ExpressContextType {
  triggerConfetti?: (source?: "checklist" | "download") => void;
  viewMode?: "intake" | "download";
  setViewMode?: (mode: "intake" | "download") => void;
}

const ExpressContext = createContext<ExpressContextType>({});

export const useExpressContext = () => useContext(ExpressContext);

const LayoutRoot = styled("div")(({ theme }) => ({
  backgroundColor: "#FAF9F8",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "top center",
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  height: "100%",
  position: "relative",
}));

interface LayoutProps {
  children?: ReactNode;
  navColor?: NavColor;
}

export const ExpressLayout: FC<LayoutProps> = (props) => {
  const { children } = props;
  const { professional_group } = useWhiteLabelConfig();
  const { ownerUserData } = useUserDataProvider();
  const isBlockedAgent =
    ownerUserData?.professional_group_role_user_data?.role === "PRO_GROUP_AGENT" &&
    (professional_group?.allow_agents_total_planning ?? true) === false;
  const { selectedHousehold } = useHousehold();
  const { ud_id } = useParams();
  const pagesUserData = selectedHousehold?.head_of_household?.ud_id === ud_id ? selectedHousehold?.head_of_household : selectedHousehold?.household_member_roles.find(member => member.role_target_user_data_ud_id === ud_id)?.role_target_user_data || null;
  const { createSetupIntent, createTrialSubscription } =
    useHousehold();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [showConfetti, setShowConfetti] = useState(false);
  const [viewMode, setViewMode] = useState<"intake" | "download">("intake");
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const navigate = useNavigate();
  const pathname = usePathname();

  // Hide switcher during onboarding
  const isOnboarding = pathname.startsWith("/onboarding");


  // Function to trigger confetti
  const triggerConfetti = useCallback((source?: "checklist" | "download") => {
    setShowConfetti(true);
    const message =
      source === "checklist"
        ? "Congratulations, you've created all documents! 🎉"
        : "Congratulations, you've signed all documents! 🎉";
    toast.success(message, {
      position: "top-center",
      duration: 5000,
    });

    // Open dialog for checklist completion
    // if (source === "checklist") {
    //   setCompletionDialogOpen(true);
    // }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (
        imageRef.current &&
        professional_group?.app_logo &&
        professional_group?.total_app_logo_multiplier
      ) {
        const { naturalWidth, naturalHeight } = imageRef.current;
        const aspectRatio = naturalWidth / naturalHeight;

        // Check if mobile screen
        const isMobile = window.innerWidth < 600; // MUI 'sm' breakpoint
        const mobileMultiplier = 0.7; // 70% of desktop size on mobile

        let maxWidth = 180;
        let maxHeight = 30;

        // Adjust maxHeight for aspect ratios
        if (aspectRatio < 2.9) {
          maxHeight = 48;
        }
        if (aspectRatio >= 2.9 && aspectRatio <= 4) {
          maxHeight = 40;
        }
        if (aspectRatio > 4) {
          maxHeight = 35;
        }

        // Apply mobile reduction
        if (isMobile) {
          maxWidth = maxWidth * mobileMultiplier;
          maxHeight = maxHeight * mobileMultiplier;
        }

        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          if (maxWidth / aspectRatio < maxHeight) {
            setDimensions({
              width: maxWidth * professional_group?.total_app_logo_multiplier,
              height:
                (maxWidth / aspectRatio) *
                professional_group?.total_app_logo_multiplier,
            });
          } else {
            setDimensions({
              width:
                maxHeight *
                aspectRatio *
                professional_group?.total_app_logo_multiplier,
              height: maxHeight * professional_group?.total_app_logo_multiplier,
            });
          }
        } else {
          setDimensions({
            width: naturalWidth * professional_group?.total_app_logo_multiplier,
            height:
              naturalHeight * professional_group?.total_app_logo_multiplier,
          });
        }
      }
    };

    const img = imageRef.current;

    if (img && professional_group?.app_logo) {
      img.onload = handleResize;
      img.src = professional_group?.app_logo;
    }

    // Add resize listener to handle screen size changes
    window.addEventListener("resize", handleResize);

    return () => {
      if (img) {
        img.onload = null;
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [
    professional_group?.app_logo,
    professional_group?.total_app_logo_multiplier,
  ]);

  const parseFirstName = (name: string | undefined): string => {
    if (name) {
      const nameArray = name.split(" ");
      return nameArray[0];
    } else {
      return "";
    }
  };

  return (
    <ExpressContext.Provider value={{ triggerConfetti, viewMode, setViewMode }}>
      <LayoutRoot>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          {showConfetti && (
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              recycle={false}
              numberOfPieces={1000}
              gravity={0.5}
              initialVelocityX={{ min: -35, max: 35 }}
              initialVelocityY={{ min: -45, max: 0 }}
              friction={0.99}
              confettiSource={{
                x: (3 * window.innerWidth) / 8,
                y: (3 * window.innerHeight) / 8,
                w: window.innerWidth / 4,
                h: window.innerHeight / 4,
              }}
              tweenDuration={500}
              run={showConfetti}
              onConfettiComplete={() => setShowConfetti(false)}
            />
          )}
        </Box>
        <Box component="header" sx={{ mt: 2, position: "relative", zIndex: 1 }}>
          <Container maxWidth="lg">
            <Stack
              direction="row"
              spacing={2}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Stack
                alignItems="center"
                component={RouterLink}
                direction="row"
                display="inline-flex"
                href={paths.dashboard.express}
                spacing={1}
                sx={{ textDecoration: "none" }}
              >
                <Box sx={{ display: "inline-flex" }}>
                  <img
                    ref={imageRef}
                    src={professional_group?.app_logo || undefined}
                    style={{
                      width: dimensions.width,
                      height: dimensions.height,
                    }}
                  />
                </Box>
              </Stack>
              <Stack direction="row" spacing={2}>
                {/* {subscriptionStatus === 'offered' &&
                <Button
                      variant="outlined"
                      size="small"
                      startIcon={<WorkspacePremiumIcon />}
                      onClick={() => setTrialDialogOpen(true)}
                    >
                      Try Premium for free
                    </Button>
                    } */}

                {!isOnboarding && !isBlockedAgent && (
                  <Box
                    sx={{
                      bgcolor: "#EBEBEB",
                      borderRadius: "10px",
                      p: "4px",
                      display: "flex",
                      position: "relative",
                      gap: "2px",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "4px",
                        left: "4px",
                        width: "calc(50% - 5px)",
                        height: "calc(100% - 8px)",
                        bgcolor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        transition:
                          "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform:
                          viewMode === "intake"
                            ? "translateX(0)"
                            : "translateX(calc(100% + 2px))",
                      }}
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        sessionStorage.setItem(
                          "totalViewPreference",
                          "express"
                        );
                        setViewMode("intake");
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        px: 2,
                        py: 0.75,
                        borderRadius: "8px",
                        color:
                          viewMode === "intake"
                            ? "text.primary"
                            : "text.secondary",
                        bgcolor: "transparent",
                        position: "relative",
                        zIndex: 1,
                        fontWeight: viewMode === "intake" ? 600 : 400,
                        transition: "color 0.2s, font-weight 0.2s",
                        "&:hover": {
                          bgcolor: "transparent",
                        },
                      }}
                      startIcon={<ChecklistIcon fontSize="small" />}
                    >
                      Checklist
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        sessionStorage.setItem(
                          "totalViewPreference",
                          "dashboard"
                        );
                        navigate(paths.dashboard.index);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        px: 2,
                        py: 0.75,
                        borderRadius: "8px",
                        color:
                          viewMode === "download"
                            ? "text.primary"
                            : "text.secondary",
                        bgcolor: "transparent",
                        position: "relative",
                        zIndex: 1,
                        fontWeight: viewMode === "download" ? 600 : 400,
                        transition: "color 0.2s, font-weight 0.2s",
                        "&:hover": {
                          bgcolor: "transparent",
                        },
                      }}
                      startIcon={<DashboardIcon fontSize="small" />}
                    >
                      Dashboard
                    </Button>
                  </Box>
                )}
                <AccountButton express={true} />
              </Stack>
            </Stack>
          </Container>
        </Box>
        <Box
          sx={{
            alignItems: "top",
            display: "flex",
            justifyContent: "center",
            flex: "1 1 auto",
            mt: 6,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent={"space-between"}
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={{ xs: 2, sm: 0 }}
              sx={{ px: { xs: 0, sm: 2, md: 4 } }}
            >
              {(pathname !== paths.dashboard.express ||
                viewMode === "download") && (
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  startIcon={<ArrowBackRoundedIcon fontSize="small" />}
                  onClick={() => {
                    if (
                      pathname === paths.dashboard.express &&
                      viewMode === "download"
                    ) {
                      setViewMode("intake");
                    } else if (!pathname.includes("express")) {
                      navigate(paths.dashboard.index);
                    } else {
                      navigate(paths.dashboard.express);
                    }
                  }}
                  sx={{ order: { xs: -1, sm: 1 } }}
                >
                  Back to Checklist
                </Button>
              )}

              <Typography color="inherit" variant="h3">
                {pagesUserData?.full_name
                  ? `Welcome, ${parseFirstName(pagesUserData?.full_name)}!`
                  : `Welcome!`}
              </Typography>
            </Stack>

            <Box sx={{ px: { xs: 0, sm: 2, md: 4 } }}>
              <Box sx={{ my: 5 }}>{children}</Box>
            </Box>
          </Container>
        </Box>

        {/* Completion Dialog */}
        <Dialog
          open={completionDialogOpen}
          onClose={() => setCompletionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">Congratulations!</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2">
                You've completed drafting all of your documents! Now you'll be
                able to review your documents, make any updates, and follow
                execution instructions to make everything official.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Box>
              <Button
                variant="contained"
                onClick={() => setCompletionDialogOpen(false)}
              >
                Continue to review
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Premium Trial Dialog */}
        <PlatformTrialDialog
          open={trialDialogOpen}
          onClose={() => setTrialDialogOpen(false)}
          createSetupIntent={createSetupIntent}
          createTrialSubscription={createTrialSubscription}
        />
      </LayoutRoot>
    </ExpressContext.Provider>
  );
};

ExpressLayout.propTypes = {
  children: PropTypes.node as PropTypes.Validator<ReactNode>,
};
